/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import { CSSProperties } from 'react';
import { fromNullable, fromPredicate, none, Option } from 'fp-ts/lib/Option';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';

import {
    applyResolutionStyle,
    divWithOpacityVisibilityClass,
    LegendActions,
    renderSimpleItemLabel,
} from '.';
import { fromRecord } from '../../locale';
import { getContext, polygonStyle } from '../../map/style';
import {
    ILayerInfo,
    Inspire,
    PatternStyle,
    PolygonDiscreteGroup,
    PolygonInterval,
    PolygonStyleConfig,
    PolygonStyleConfigContinuous,
    PolygonStyleConfigDiscrete,
    PolygonStyleConfigSimple,
} from '../../source';
import { notEmpty } from '../../util';
import { DIV, NodeOrOptional, SPAN } from '../elements';
import { getAlphaNb } from '../input';

const logger = debug('sdi:legend-polygon');

const polygonGeometry = new Polygon([
    [
        [0, 0],
        [100, 0],
        [100, 100],
        [0, 100],
        [0, 0],
    ],
]);

interface Stroke {
    width: number;
    color: string;
}

type StrokeSource = {
    strokeWidth: number;
    strokeColor: string;
    pattern?: PatternStyle;
};

const makeStroke = (config: StrokeSource): Stroke | null => {
    if (config.strokeWidth >= 0.5) {
        // if (config.pattern && config.patternColor === undefined) {
        //     return null;
        // }
        return {
            width: config.strokeWidth,
            color: config.strokeColor,
        };
    }
    return null;
};

const hashAnything = (...args: unknown[]) =>
    args.map(arg => JSON.stringify(arg)).join('|');

const IMG_URL_CACHE: Record<string, string> = {};

const withCache = (
    hash: string,
    then: (url: string) => NodeOrOptional,
    orElse: (h: string) => NodeOrOptional,
) => {
    if (hash in IMG_URL_CACHE) {
        return then(IMG_URL_CACHE[hash]);
    }
    return orElse(hash);
};

const itemStyle = (
    url: string,
    width?: number,
    color?: string,
): CSSProperties => ({
    width: '100%',
    height: '50%',
    backgroundImage: `url(${url})`,
    backgroundPosition: 'center',
    borderStyle: 'solid',
    borderWidth: width === undefined ? '0' : `${width}px`,
    borderColor: color === undefined ? 'inherit' : color,
});

const item = (
    hash: string,
    canvas: HTMLCanvasElement,
    label: string,
    stroke: Stroke | null,
    actions: NodeOrOptional,
) =>
    DIV(
        `legend-item polygon`,
        actions,
        DIV(
            'item-style',
            withCache(
                hash,
                url =>
                    DIV({
                        style: itemStyle(url, stroke?.width, stroke?.color),
                    }),
                hash =>
                    DIV({
                        style: itemStyle('', stroke?.width, stroke?.color),
                        ref: div => {
                            fromNullable(div).map(div => {
                                canvas.toBlob(blob =>
                                    fromNullable(blob).map(blob => {
                                        const url = URL.createObjectURL(blob);
                                        IMG_URL_CACHE[hash] = url;
                                        div.style.backgroundImage = `url(${url})`;
                                    }),
                                );
                            });
                        },
                    }),
            ),
        ),
        DIV('item-label', SPAN({}, label)),
    );

const renderSimple = (
    config: PolygonStyleConfigSimple,
    layerInfo: ILayerInfo,
    md: Option<Inspire>,
    actions: LegendActions,
) =>
    fromNullable(getContext(100, 100)).map(ctx => {
        const { canvas, olContext } = ctx;
        const styles = applyResolutionStyle(
            polygonStyle(config),
            new Feature(polygonGeometry),
        );
        styles.forEach(style => {
            olContext.setStyle(style);
            olContext.drawGeometry(polygonGeometry);
        });
        const layerAction = fromNullable(actions.layer)
            .map(action => action(layerInfo))
            .getOrElse(none);

        const label = renderSimpleItemLabel(layerInfo, md);

        return divWithOpacityVisibilityClass(
            layerInfo,
            0,
            item(hashAnything(config), canvas, label, makeStroke(config), none),
            layerAction,
        );
    });

type PolygonItem = PolygonDiscreteGroup | PolygonInterval;
const allSameOpacity = (items: PolygonItem[]) =>
    notEmpty(items)
        .map(elements =>
            elements.every(
                g =>
                    getAlphaNb(g.fillColor)() ===
                    getAlphaNb(items[0].fillColor)(),
            ),
        )
        .getOrElse(true);

const renderDiscrete = (
    config: PolygonStyleConfigDiscrete,
    layerInfo: ILayerInfo,
    _md: Option<Inspire>,
    actions: LegendActions,
) => {
    const styleFn = polygonStyle(config);

    const itemAction = (idx: number) =>
        fromNullable(actions.item)
            .map(action => action(layerInfo, idx))
            .getOrElse(none);

    const renderOpacitySelector = fromNullable(actions.layer)
        .map(op => op(layerInfo))
        .getOrElse(none);

    const groups = config.groups;

    return notEmpty(groups).map(gps => {
        const items = gps.map((group, groupIdx) =>
            notEmpty(group.values).chain(() =>
                fromNullable(getContext(100, 100)).map(ctx => {
                    const { canvas, canvasContext, olContext } = ctx;
                    canvasContext.clearRect(0, 0, 100, 100);
                    const f = new Feature(polygonGeometry);
                    f.set(config.propName, group.values[0]);
                    const styles = applyResolutionStyle(styleFn, f);
                    styles.forEach(style => {
                        olContext.drawFeature(f, style);
                    });
                    const stroke = group.hidden ? null : makeStroke(group);
                    return divWithOpacityVisibilityClass(
                        layerInfo,
                        groupIdx,
                        item(
                            hashAnything(config, groupIdx),
                            canvas,
                            fromRecord(group.label),
                            stroke,
                            itemAction(groupIdx),
                        ),
                    );
                }),
            ),
        );
        // TODO: use this to only have layer opacity selector when all items have the same default opacity
        // const renderOpacity = fromPredicate<PolygonDiscreteGroup[]>(gList =>
        //     allSameOpacity(gList)
        // )(groups).chain(() => opacitySelectorOpt.map(op => op(layerInfo)));
        return DIV('items', renderOpacitySelector, ...items);
    });
};

const renderContinuous = (
    config: PolygonStyleConfigContinuous,
    layerInfo: ILayerInfo,
    _md: Option<Inspire>,
    actions: LegendActions,
) => {
    const styleFn = polygonStyle(config);
    const itemAction = (idx: number) =>
        fromNullable(actions.item)
            .map(action => action(layerInfo, idx))
            .getOrElse(none);
    const opacitySelectorOpt = fromNullable(actions.layer);

    const intervals = config.intervals;
    return notEmpty(intervals).map(() => {
        const items = intervals.map((interval, idx) =>
            fromNullable(getContext(100, 100)).map(ctx => {
                const { canvas, canvasContext, olContext } = ctx;
                canvasContext.clearRect(0, 0, 100, 100);
                const f = new Feature(polygonGeometry);
                const v = interval.low + (interval.high - interval.low) / 2;
                f.set(config.propName, v);
                const styles = applyResolutionStyle(styleFn, f);
                styles.forEach(style => {
                    olContext.drawFeature(f, style);
                });
                const stroke = interval.hidden ? null : makeStroke(interval);
                return divWithOpacityVisibilityClass(
                    layerInfo,
                    idx,
                    item(
                        hashAnything(config, idx),
                        canvas,
                        fromRecord(interval.label),
                        stroke,
                        itemAction(idx),
                    ),
                );
            }),
        );
        const opacity = fromPredicate<PolygonInterval[]>(iList =>
            allSameOpacity(iList),
        )(intervals)
            .chain(() => opacitySelectorOpt.map(op => op(layerInfo)))
            .getOrElse(none);
        return DIV('items', opacity, ...items);
    });
};

const render = (
    config: PolygonStyleConfig,
    layerInfo: ILayerInfo,
    md: Option<Inspire>,
    actions = {} as LegendActions,
) => {
    switch (config.kind) {
        case 'polygon-simple':
            return renderSimple(config, layerInfo, md, actions);
        case 'polygon-discrete':
            return renderDiscrete(config, layerInfo, md, actions);
        case 'polygon-continuous':
            return renderContinuous(config, layerInfo, md, actions);
    }
};

export default render;

logger('loaded');
